/**
 * The trainer's own library of plan templates, as `queryOptions`.
 *
 * ── Promoted out of `pages/plan-library/api/` on its SECOND consumer, 21 September 2026 ──────
 * The assign dialog's client-first entry point asks the same question: opened from a client who
 * has no plan, it offers the library to pick FROM, where the builder entry point already knows
 * which plan is being assigned. A page may not be imported by a feature, and a second copy of this
 * `queryOptions` would be a second cache entry for one endpoint — the whole library fetched twice
 * and two answers that can disagree. The key is unchanged, so the promotion moves no behaviour.
 *
 * Everything below is the page's own note, moved unedited.
 *
 * ── Hand-written rather than `getGetPlanTemplateLibraryQueryOptions` ─────────────────────────
 * The generated options exist and are correct as of the PR-A0 regen. They are not used because
 * the key is a decision this slice owns: the screen fetches the library WHOLE and filters it in
 * `model/planLibraryView.ts`, so the key must be constant, and a key derived from the search box
 * would silently reintroduce a request per keystroke the moment somebody passed the term
 * through. Delegating would also hand the cache key to whatever orval emits next, which is not
 * a thing a screen's caching behaviour should depend on.
 *
 * ── The key is `['plan-template', 'library']`, with nothing in it ────────────────────────────
 * The PR brief specified `['plan-template', 'library', { search, category }, lang]`. Two of
 * those cannot be right here and the third does not exist:
 *
 *   · `search` and `category` are real server parameters, and v1 deliberately sends neither —
 *     see `model/planLibraryView.ts` for why fetch-all wins on a trainer's own template list.
 *     Putting always-`undefined` values in the key would be dead structure that reads as if
 *     filtering were server-side, which is the misunderstanding that costs the next reader an
 *     hour.
 *   · `lang` is not a parameter of this endpoint at all. `PlanTemplateController.ListLibrary`
 *     takes `search` and `category` only, and `PlanTemplateListItem` carries no server-localised
 *     text — the category enum it does carry is translated on this side, from the trainer's own
 *     locale bundle. There is nothing to key on. `src/shared/api/generatedContract.test.ts`
 *     asserts the exact URL this read produces, which pins the absence from THIS side; a `lang`
 *     appearing on the backend's side would show up in the regen diff, not in that test.
 *
 * A constant key is also what makes the whole-library fetch honest: one entry in the cache, one
 * request per staleness window, and a search box that costs nothing.
 */

import { queryOptions } from '@tanstack/react-query';

import { getPlanTemplateLibrary } from '~/shared/api/generated/endpoints/plan-template/plan-template';

/**
 * The key, exported so the first thing that ever invalidates this read uses the constant rather
 * than restating the array. Nothing does yet: the two readers only read.
 */
export const planLibraryQueryKey = ['plan-template', 'library'] as const;

export function planLibraryQueryOptions() {
  return queryOptions({
    queryKey: planLibraryQueryKey,
    // No params object at all, rather than `{}`: the mutator only appends a query string when
    // `params` is present, so this sends a bare `GET /plan-template/library`.
    queryFn: ({ signal }) => getPlanTemplateLibrary(undefined, undefined, signal),
  });
}
